import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  createStyles,
  Divider,
  FileInput,
  Flex,
  Grid,
  Group,
  Input,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Progress,
  RingProgress,
  ScrollArea,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { useSelector } from "react-redux";
import {
  IconSearch, IconEdit, IconTrash, IconFileDownload,
  IconShieldCheck, IconClipboardCheck, IconCheckupList,
  IconAlertTriangle, IconCalendar, IconBuildingHospital,
  IconChevronDown, IconChevronRight,
} from "@tabler/icons-react";
import { FaPlus } from "react-icons/fa6";
import moment from "moment";
import swal from "sweetalert";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import RecordNotFound from "../../../components/RecordNotFound";
import { NewAddButton } from "../../../components/AddButton/NewAddButton";
import {
  getComplianceDashboard,
  getComplianceAlerts,
  getCredentialTypes,
  getStaffCredentialsOverview,
  getFacilityDocumentsOverview,
  getClientDocumentsOverview,
  getClientComplianceItems,
  getFacilityComplianceItems,
  getComplianceSettings,
  updateComplianceSettings,
  addCredentialType,
  addClientComplianceItem,
  updateClientComplianceItem,
  deleteClientComplianceItem,
  addFacilityComplianceItem,
  updateFacilityComplianceItem,
  deleteFacilityComplianceItem,
  runComplianceAudit,
  approveClientComplianceItem,
  approveFacilityComplianceItem,
} from "../../../services/compliance";
import { getAllFacilitiesModal } from "../../../services/facility";
import { getAllNoteTypes } from "../../../services/note";
import { getTaskTypes } from "../../../services/task";
import { uploadFileToS3Api } from "../../../utils/PresignedUrlFunction";
import CustomBillingPagination from "../../../components/Pagination/CustomBillingPagination";
import { https } from "../../../http";

const useStyles = createStyles(() => ({
  content: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    overflowX: "auto",
    backgroundColor: "#F5F5F5",
    minHeight: "100vh",
  },
  mainContainer: {
    padding: "30px",
    "@media (max-width:900px)": {
      paddingRight: "10px",
      paddingLeft: "10px",
    },
  },
  tabsBar: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderRadius: "12px",
    padding: "27px 16px",
    background: "#FFF",
    marginBottom: "20px",
  },
  tabContainer: {
    display: "flex",
    alignItems: "center",
  },
  activeTabBg: {
    borderRadius: "4px 4px 0px 0px",
    borderBottom: "2px solid #171717",
    background: "#FFF",
  },
  tabBtn: {
    color: "#000",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
    cursor: "pointer",
    padding: "8px 16px",
  },
  title: {
    color: "#171717",
    fontFamily: "Inter",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    marginTop: "15px",
  },
  table: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    borderRadius: "12px",
    padding: "0 10px",
    background: "#FFFFFF",
    border: "1px solid #E5E5E5",
    marginTop: "10px",
  },
  search: {
    background: "#FFF",
    marginBottom: "10px",
    borderRadius: "12px",
  },
  searchInput: {
    width: "100%",
    marginBlock: "10px",
    borderRadius: "12px",
    "& :focus-within": { border: "1px solid #D0D5DD" },
    "& :focus": { border: "1px solid #D0D5DD" },
    "& :hover": { border: "1px solid #D0D5DD" },
    "& :active": { border: "1px solid #D0D5DD" },
    "& :focus-visible": { border: "1px solid #D0D5DD" },
  },
  statCard: {
    padding: 20,
    borderRadius: 12,
    border: "1px solid #E5E5E5",
    backgroundColor: "#FFF",
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    fontFamily: "Inter",
    color: "#262626",
  },
  statLabel: {
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "Inter",
    color: "#737373",
  },
  statusBadge: {
    textTransform: "capitalize",
    fontFamily: "Inter",
    fontWeight: 500,
  },
  sectionTitle: {
    color: "#171717",
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "24px",
    marginTop: "15px",
    marginBottom: "12px",
  },
  settingsCard: {
    background: "#fff",
    margin: "0",
    borderRadius: "12px",
    padding: "12px 26px",
  },
  settingsTitle: {
    color: "#262626",
    textAlign: "left",
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  settingsLabel: {
    color: "#344054",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    marginBottom: 4,
  },
  settingsDescription: {
    color: "#737373",
    fontFamily: "Inter",
    fontSize: "13px",
    fontWeight: 400,
    marginBottom: 8,
  },
  settingsBtnGroup: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    margin: "1rem 0",
  },
  // ── Dashboard Styles ──
  dashCard: {
    padding: 24,
    borderRadius: 16,
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFF",
    transition: "box-shadow 0.2s",
    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
  },
  overallScoreCard: {
    padding: 28,
    borderRadius: 16,
    background: "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
    color: "#FFF",
    position: "relative",
    overflow: "hidden",
  },
  scoreCardTitle: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#6B7280",
    marginBottom: 8,
  },
  scoreCardValue: {
    fontFamily: "Inter",
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  scoreCardSub: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    color: "#9CA3AF",
    marginTop: 4,
  },
  scoreBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: "#F3F4F6",
    overflow: "hidden",
    marginTop: 12,
  },
  alertItem: {
    padding: "12px 16px",
    borderRadius: 10,
    backgroundColor: "#F9FAFB",
    border: "1px solid #F3F4F6",
    marginBottom: 8,
    "&:last-child": { marginBottom: 0 },
  },
  severityBadge: {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  facilityBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": { borderBottom: "none" },
  },
  summaryStatCard: {
    padding: 20,
    borderRadius: 14,
    border: "1px solid #E5E7EB",
    backgroundColor: "#FFF",
    textAlign: "center",
  },
}));

const statusColor = (status) => {
  switch (status) {
    case "compliant": return "green";
    case "expiring": return "yellow";
    case "expired": return "red";
    case "missing": return "gray";
    case "non_compliant": return "red";
    default: return "gray";
  }
};

const FACILITY_CATEGORY_OPTIONS = [
  { value: "license", label: "License" },
  { value: "inspection", label: "Inspection" },
  { value: "drill", label: "Drill" },
  { value: "insurance", label: "Insurance" },
  { value: "training", label: "Training" },
  { value: "document", label: "Document" },
  { value: "other", label: "Other" },
];

const CLIENT_CATEGORY_OPTIONS = [
  { value: "document", label: "Document" },
  { value: "notes", label: "Notes" },
  { value: "task", label: "Task" },
  { value: "mar", label: "MAR" },
];

const ITEM_TYPE_OPTIONS = [
  { value: "staff", label: "Staff Document" },
  { value: "facility", label: "Facility Document" },
  { value: "client", label: "Client Document" },
];

const AddComplianceItemModal = ({ opened, onClose, addLoading, facilities, noteTypes, taskTypes }) => {
  const [itemType, setItemType] = useState("staff");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Facility fields
  const [category, setCategory] = useState("other");
  const [facility, setFacility] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Client fields
  const [clientCategory, setClientCategory] = useState("document");
  const [threshold, setThreshold] = useState(95);
  const [frequencyMonths, setFrequencyMonths] = useState(12);
  const [gracePeriodDays, setGracePeriodDays] = useState(30);
  const [noteType, setNoteType] = useState(null);
  const [taskType, setTaskType] = useState(null);

  const resetForm = () => {
    setName(""); setDescription(""); setCategory("other"); setFacility(null);
    setExpirationDate(null); setFile(null); setUploading(false);
    setClientCategory("document"); setThreshold(95); setFrequencyMonths(12);
    setGracePeriodDays(30); setNoteType(null); setTaskType(null);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    let success;

    if (itemType === "staff") {
      success = await addCredentialType({ name: name.trim(), description: description.trim() });
    } else if (itemType === "facility") {
      let documentUrl = "";
      let fileName = "";
      if (file) {
        try {
          setUploading(true);
          const result = await uploadFileToS3Api({ file });
          documentUrl = result.uploadURL || result.path;
          fileName = file.name;
          setUploading(false);
        } catch (err) {
          setUploading(false);
          swal({ title: "File upload failed", text: err.message, icon: "error" });
          return;
        }
      }
      success = await addFacilityComplianceItem({
        name: name.trim(), description: description.trim(), category,
        facility: facility || undefined, documentUrl, fileName,
        expirationDate: expirationDate ? expirationDate.toISOString() : undefined,
      });
    } else if (itemType === "client") {
      const payload = { name: name.trim(), description: description.trim(), category: clientCategory };
      if (clientCategory === "document") {
        payload.frequencyMonths = frequencyMonths;
        payload.gracePeriodDays = gracePeriodDays;
      } else {
        payload.threshold = threshold;
      }
      if (clientCategory === "notes" && noteType) payload.noteType = noteType;
      if (clientCategory === "task" && taskType) payload.taskType = taskType;
      success = await addClientComplianceItem(payload);
    }

    if (success) { resetForm(); onClose(); }
  };

  const isLoading = addLoading || uploading;
  const labelStyle = { label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } };

  return (
    <Modal opened={opened} onClose={() => { resetForm(); onClose(); }} title="Add Compliance Item" centered size="lg"
      styles={{ title: { fontFamily: "Inter", fontWeight: 600, fontSize: 18 } }}>
      <Select label="Item Type" placeholder="Select type" data={ITEM_TYPE_OPTIONS} value={itemType}
        onChange={(v) => { setItemType(v); resetForm(); }} styles={labelStyle} mb={12} />

      <TextInput label="Name" placeholder="Enter name" required value={name}
        onChange={(e) => setName(e.currentTarget.value)} styles={labelStyle} mb={12} />

      {itemType === "facility" && (
        <>
          <Grid mb={12}>
            <Grid.Col span={6}>
              <Select label="Category" placeholder="Select category" data={FACILITY_CATEGORY_OPTIONS}
                value={category} onChange={setCategory} styles={labelStyle} />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select label="Facility" placeholder="Select facility (optional)" clearable
                data={(facilities || []).map((f) => ({ value: f._id || f.id, label: f.name }))}
                value={facility} onChange={setFacility} styles={labelStyle} />
            </Grid.Col>
          </Grid>
        </>
      )}

      {itemType === "client" && (
        <>
          <Select label="Category" placeholder="Select category" data={CLIENT_CATEGORY_OPTIONS}
            value={clientCategory} onChange={setClientCategory} required styles={labelStyle} mb={12} />
          {clientCategory === "notes" && (
            <Select label="Note Type" placeholder="Select note type" searchable required
              data={(noteTypes || []).map((nt) => ({ value: nt._id || nt.id, label: nt.name }))}
              value={noteType} onChange={setNoteType} styles={labelStyle} mb={12} />
          )}
          {clientCategory === "task" && (
            <Select label="Task Type" placeholder="Select task type" searchable required
              data={(taskTypes || []).map((tt) => ({ value: tt._id || tt.id, label: tt.name }))}
              value={taskType} onChange={setTaskType} styles={labelStyle} mb={12} />
          )}
        </>
      )}

      <Textarea label="Description" placeholder="Enter description (optional)" value={description}
        onChange={(e) => setDescription(e.currentTarget.value)} styles={labelStyle} mb={12} minRows={2} />

      {itemType === "facility" && (
        <Grid mb={12}>
          <Grid.Col span={6}>
            <FileInput label="Upload Document" placeholder="Choose file" value={file} onChange={setFile} clearable
              accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx" styles={labelStyle} />
          </Grid.Col>
          <Grid.Col span={6}>
            <DatePickerInput label="Expiration Date" placeholder="Select date" value={expirationDate}
              onChange={setExpirationDate} clearable styles={labelStyle} />
          </Grid.Col>
        </Grid>
      )}

      {itemType === "client" && clientCategory === "document" && (
        <Grid mb={12}>
          <Grid.Col span={6}>
            <NumberInput label="Renewal Frequency (months)" placeholder="12" min={1} max={120}
              value={frequencyMonths} onChange={setFrequencyMonths} radius="md" styles={labelStyle} />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput label="Grace Period (days)" placeholder="30" min={0} max={365}
              value={gracePeriodDays} onChange={setGracePeriodDays} radius="md" styles={labelStyle} />
          </Grid.Col>
        </Grid>
      )}

      {itemType === "client" && clientCategory !== "document" && (
        <NumberInput label="Completion Threshold (%)" placeholder="95" min={0} max={100}
          value={threshold} onChange={setThreshold} radius="md" mb={12}
          description={"Clients below " + threshold + "% completion will be flagged as non-compliant"}
          styles={labelStyle} />
      )}

      <Flex justify="flex-end" gap={12} mt={16}>
        <Button variant="filled" onClick={() => { resetForm(); onClose(); }}
          bg="#F9FAFB" c="#1D2939" radius="md" size="md"
          style={{ border: "1px solid #D0D5DD", fontFamily: "Inter" }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isLoading} disabled={!name.trim()}
          variant="filled" color="yellow" c="#000" radius="md" size="md"
          style={{ fontFamily: "Inter" }}>
          Save
        </Button>
      </Flex>
    </Modal>
  );
};

const FacilityRequirementModal = ({ opened, onClose, editItem, facilities, addLoading }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("other");
  const [facility, setFacility] = useState(null);
  const [expirationDate, setExpirationDate] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [existingDocUrl, setExistingDocUrl] = useState("");
  const [existingFileName, setExistingFileName] = useState("");
  const [approvedAt, setApprovedAt] = useState(null);
  const [approvedBy, setApprovedBy] = useState(null);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (editItem) {
      setName(editItem.name || "");
      setDescription(editItem.description || "");
      setCategory(editItem.category || "other");
      setFacility(editItem.facility?._id || editItem.facility || null);
      setExpirationDate(editItem.expirationDate ? new Date(editItem.expirationDate) : null);
      setExistingDocUrl(editItem.documentUrl || "");
      setExistingFileName(editItem.fileName || "");
      setFile(null);
      setApprovedAt(editItem.approvedAt || null);
      setApprovedBy(editItem.approvedBy || null);
    } else {
      resetForm();
    }
  }, [editItem, opened]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setCategory("other");
    setFacility(null);
    setExpirationDate(null);
    setFile(null);
    setExistingDocUrl("");
    setExistingFileName("");
    setUploading(false);
    setApprovedAt(null);
    setApprovedBy(null);
    setApproving(false);
  };

  const handleApprove = async () => {
    if (!editItem) return;
    setApproving(true);
    const result = await approveFacilityComplianceItem(editItem._id || editItem.id);
    if (result) {
      setApprovedAt(result.approvedAt);
      setApprovedBy(result.approvedBy);
    }
    setApproving(false);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;

    let documentUrl = existingDocUrl;
    let fileName = existingFileName;

    if (file) {
      try {
        setUploading(true);
        const result = await uploadFileToS3Api({ file });
        documentUrl = result.uploadURL || result.path;
        fileName = file.name;
        setUploading(false);
      } catch (err) {
        setUploading(false);
        swal({ title: "File upload failed", text: err.message, icon: "error" });
        return;
      }
    }

    const payload = {
      name: name.trim(),
      description: description.trim(),
      category,
      facility: facility || undefined,
      documentUrl,
      fileName,
      expirationDate: expirationDate ? expirationDate.toISOString() : undefined,
    };

    let success;
    if (editItem) {
      success = await updateFacilityComplianceItem(editItem._id || editItem.id, payload);
    } else {
      success = await addFacilityComplianceItem(payload);
    }

    if (success) {
      resetForm();
      onClose();
    }
  };

  const isLoading = addLoading || uploading;

  return (
    <Modal opened={opened} onClose={() => { resetForm(); onClose(); }}
      title={editItem ? "Edit Requirement" : "Add Facility Requirement"} centered size="lg"
      styles={{ title: { fontFamily: "Inter", fontWeight: 600, fontSize: 18 } }}>
      <TextInput label="Requirement Name" placeholder="e.g. Fire Inspection Certificate" required value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      <Grid mb={12}>
        <Grid.Col span={6}>
          <Select label="Category" placeholder="Select category" data={FACILITY_CATEGORY_OPTIONS} value={category}
            onChange={setCategory}
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select label="Facility" placeholder="Select facility (optional)" clearable
            data={(facilities || []).map((f) => ({ value: f._id || f.id, label: f.name }))}
            value={facility} onChange={setFacility}
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
        </Grid.Col>
      </Grid>
      <Textarea label="Description" placeholder="Enter description (optional)" value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} minRows={2} />
      <Grid mb={12}>
        <Grid.Col span={6}>
          <FileInput label="Upload Document" placeholder={existingFileName || "Choose file"} value={file}
            onChange={setFile} clearable
            accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
          {existingFileName && !file && (
            <Text fz={12} color="dimmed" mt={4}>Current: {existingFileName}</Text>
          )}
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput label="Expiration Date" placeholder="Select date" value={expirationDate}
            onChange={setExpirationDate} clearable
            styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
        </Grid.Col>
      </Grid>
      {editItem && (
        <Box mt={12} p="sm" style={{ background: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB" }}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text fz={11} fw={700} ff="Inter" color="#6B7280" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Ogiso Approval
              </Text>
              {approvedAt ? (
                <Text fz={12} ff="Inter" color="#374151" mt={2}>
                  Last approved: {moment(approvedAt).format("MMM DD, YYYY")}
                  {approvedBy?.name ? ` by ${approvedBy.name}` : ""}
                </Text>
              ) : (
                <Text fz={12} ff="Inter" color="#9CA3AF" mt={2}>Not yet approved by Ogiso</Text>
              )}
            </Box>
            <Button size="xs" variant="light" color="green" loading={approving} onClick={handleApprove}
              leftIcon={<IconShieldCheck size={13} />}
              styles={{ root: { fontFamily: "Inter", fontSize: 12 } }}>
              {approvedAt ? "Re-approve" : "Approve"}
            </Button>
          </Flex>
        </Box>
      )}
      <Flex justify="flex-end" gap={12} mt={16}>
        <Button variant="filled" onClick={() => { resetForm(); onClose(); }}
          bg="#F9FAFB" c="#1D2939" radius="md" size="md"
          style={{ border: "1px solid #D0D5DD", fontFamily: "Inter" }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isLoading} disabled={!name.trim()}
          variant="filled" color="yellow" c="#000" radius="md" size="md"
          style={{ fontFamily: "Inter" }}>
          {editItem ? "Update" : "Save"}
        </Button>
      </Flex>
    </Modal>
  );
};

const ClientComplianceItemModal = ({ opened, onClose, editItem, addLoading, noteTypes, taskTypes, defaultCategory }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("document");
  const [threshold, setThreshold] = useState(95);
  const [frequencyMonths, setFrequencyMonths] = useState(12);
  const [gracePeriodDays, setGracePeriodDays] = useState(30);
  const [noteType, setNoteType] = useState(null);
  const [taskType, setTaskType] = useState(null);
  const [approvedAt, setApprovedAt] = useState(null);
  const [approvedBy, setApprovedBy] = useState(null);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (editItem) {
      setName(editItem.name || "");
      setDescription(editItem.description || "");
      setCategory(editItem.category || "document");
      setThreshold(editItem.threshold ?? 95);
      setFrequencyMonths(editItem.frequencyMonths ?? 12);
      setGracePeriodDays(editItem.gracePeriodDays ?? 30);
      setNoteType(editItem.noteType?._id || editItem.noteType || null);
      setTaskType(editItem.taskType?._id || editItem.taskType || null);
      setApprovedAt(editItem.approvedAt || null);
      setApprovedBy(editItem.approvedBy || null);
    } else {
      setName("");
      setDescription("");
      setCategory(defaultCategory || "document");
      setThreshold(95);
      setFrequencyMonths(12);
      setGracePeriodDays(30);
      setNoteType(null);
      setTaskType(null);
      setApprovedAt(null);
      setApprovedBy(null);
    }
  }, [editItem, opened, defaultCategory]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setCategory(defaultCategory || "document");
    setThreshold(95);
    setFrequencyMonths(12);
    setGracePeriodDays(30);
    setNoteType(null);
    setTaskType(null);
    setApprovedAt(null);
    setApprovedBy(null);
    setApproving(false);
  };

  const handleApprove = async () => {
    if (!editItem) return;
    setApproving(true);
    const result = await approveClientComplianceItem(editItem._id || editItem.id);
    if (result) {
      setApprovedAt(result.approvedAt);
      setApprovedBy(result.approvedBy);
    }
    setApproving(false);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !category) return;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      category,
    };

    if (category === "document") {
      payload.frequencyMonths = frequencyMonths;
      payload.gracePeriodDays = gracePeriodDays;
    } else {
      payload.threshold = threshold;
    }

    if (category === "notes" && noteType) {
      payload.noteType = noteType;
    }
    if (category === "task" && taskType) {
      payload.taskType = taskType;
    }

    let success;
    if (editItem) {
      success = await updateClientComplianceItem(editItem._id || editItem.id, payload);
    } else {
      success = await addClientComplianceItem(payload);
    }

    if (success) {
      resetForm();
      onClose();
    }
  };

  const isDocument = category === "document";
  const isNotes = category === "notes";
  const isTask = category === "task";

  return (
    <Modal opened={opened} onClose={() => { resetForm(); onClose(); }}
      title={editItem ? "Edit Compliance Requirement" : defaultCategory === "document" ? "Add Client Document Requirement" : "Add Compliance Requirement"} centered size="md"
      styles={{ title: { fontFamily: "Inter", fontWeight: 600, fontSize: 18 } }}>
      <TextInput label="Requirement Name" placeholder="e.g. Progress Notes, TB Test, Daily Tasks" required value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      <Select label="Category" placeholder="Select category"
        data={defaultCategory === "document"
          ? CLIENT_CATEGORY_OPTIONS.filter(o => o.value === "document")
          : defaultCategory && defaultCategory !== "document"
            ? CLIENT_CATEGORY_OPTIONS.filter(o => ["notes", "task", "mar"].includes(o.value))
            : CLIENT_CATEGORY_OPTIONS}
        value={category} onChange={setCategory} required
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />

      {isNotes && (
        <Select label="Note Type" placeholder="Select note type to track" searchable required
          data={(noteTypes || []).map((nt) => ({ value: nt._id || nt.id, label: nt.name }))}
          value={noteType} onChange={setNoteType}
          styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      )}

      {isTask && (
        <Select label="Task Type" placeholder="Select task type to track" searchable required
          data={(taskTypes || []).map((tt) => ({ value: tt._id || tt.id, label: tt.name }))}
          value={taskType} onChange={setTaskType}
          styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} />
      )}

      <Textarea label="Description" placeholder="Enter description (optional)" value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
        styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} mb={12} minRows={2} />

      {isDocument ? (
        <Grid mb={12}>
          <Grid.Col span={6}>
            <NumberInput label="Renewal Frequency (months)" placeholder="12" min={1} max={120}
              value={frequencyMonths} onChange={setFrequencyMonths} radius="md"
              styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput label="Grace Period (days)" placeholder="30" min={0} max={365}
              value={gracePeriodDays} onChange={setGracePeriodDays} radius="md"
              styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
          </Grid.Col>
        </Grid>
      ) : (
        <NumberInput label="Completion Threshold (%)" placeholder="95" min={0} max={100}
          value={threshold} onChange={setThreshold} radius="md" mb={12}
          description={`Clients below ${threshold}% completion will be flagged as non-compliant`}
          styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }} />
      )}

      {editItem && (
        <Box mt={12} p="sm" style={{ background: "#F9FAFB", borderRadius: 8, border: "1px solid #E5E7EB" }}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text fz={11} fw={700} ff="Inter" color="#6B7280" style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Ogiso Approval
              </Text>
              {approvedAt ? (
                <Text fz={12} ff="Inter" color="#374151" mt={2}>
                  Last approved: {moment(approvedAt).format("MMM DD, YYYY")}
                  {approvedBy?.name ? ` by ${approvedBy.name}` : ""}
                </Text>
              ) : (
                <Text fz={12} ff="Inter" color="#9CA3AF" mt={2}>Not yet approved by Ogiso</Text>
              )}
            </Box>
            <Button size="xs" variant="light" color="green" loading={approving} onClick={handleApprove}
              leftIcon={<IconShieldCheck size={13} />}
              styles={{ root: { fontFamily: "Inter", fontSize: 12 } }}>
              {approvedAt ? "Re-approve" : "Approve"}
            </Button>
          </Flex>
        </Box>
      )}
      <Flex justify="flex-end" gap={12} mt={16}>
        <Button variant="filled" onClick={() => { resetForm(); onClose(); }}
          bg="#F9FAFB" c="#1D2939" radius="md" size="md"
          style={{ border: "1px solid #D0D5DD", fontFamily: "Inter" }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={addLoading} disabled={!name.trim() || !category}
          variant="filled" color="yellow" c="#000" radius="md" size="md"
          style={{ fontFamily: "Inter" }}>
          {editItem ? "Update" : "Save"}
        </Button>
      </Flex>
    </Modal>
  );
};

const CompliancePage = () => {
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState("overview");
  const [currentPage, setCurrentPage] = useState(1);
  const [facilityDocPage, setFacilityDocPage] = useState(1);
  const [facilityDocSearch, setFacilityDocSearch] = useState("");
  const [expandedStaff, setExpandedStaff] = useState(null);
  const [expandedStaffDocs, setExpandedStaffDocs] = useState([]);
  const [expandedStaffLoading, setExpandedStaffLoading] = useState(false);
  const [expandedFacility, setExpandedFacility] = useState(null);
  const [expandedFacilityDocs, setExpandedFacilityDocs] = useState([]);
  const [expandedFacilityLoading, setExpandedFacilityLoading] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);
  const [expandedClientDocs, setExpandedClientDocs] = useState([]);
  const [expandedClientLoading, setExpandedClientLoading] = useState(false);
  const [clientDocPage, setClientDocPage] = useState(1);
  const [clientDocSearch, setClientDocSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [facilityModalOpened, { open: openFacilityModal, close: closeFacilityModal }] = useDisclosure(false);
  const [editingFacilityItem, setEditingFacilityItem] = useState(null);
  const [facilitySearch, setFacilitySearch] = useState("");
  const [clientModalOpened, { open: openClientModal, close: closeClientModal }] = useDisclosure(false);
  const [editingClientItem, setEditingClientItem] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientItemCategory, setClientItemCategory] = useState(null);

  const handleEditFacilityItem = (item) => {
    setEditingFacilityItem(item);
    openFacilityModal();
  };

  const handleAddFacilityItem = () => {
    setEditingFacilityItem(null);
    openFacilityModal();
  };

  const handleDeleteFacilityItem = async (item) => {
    const willDelete = await swal({
      title: "Delete Requirement?",
      text: `Are you sure you want to delete "${item.name}"?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (willDelete) {
      await deleteFacilityComplianceItem(item._id || item.id);
    }
  };

  const handleEditClientItem = (item) => {
    setEditingClientItem(item);
    setClientItemCategory(item.category || null);
    openClientModal();
  };

  const handleAddClientItem = (defaultCat = null) => {
    setEditingClientItem(null);
    setClientItemCategory(defaultCat);
    openClientModal();
  };

  const handleDeleteClientItem = async (item) => {
    const willDelete = await swal({
      title: "Delete Compliance Item?",
      text: `Are you sure you want to delete "${item.name}"?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (willDelete) {
      await deleteClientComplianceItem(item._id || item.id);
    }
  };

  // Settings form state
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingRule, setTogglingRule] = useState(null);

  // Audit modal state
  const [auditModalOpened, { open: openAuditModal, close: closeAuditModal }] = useDisclosure(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditType, setAuditType] = useState(null);

  const handleRunAudit = async (type) => {
    setAuditType(type);
    setAuditData(null);
    setAuditLoading(true);
    openAuditModal();
    const data = await runComplianceAudit(type);
    setAuditData(data);
    setAuditLoading(false);
  };

  const getAuditTitle = (type) => {
    switch (type) {
      case "notes": return "Progress Note Audit";
      case "tasks": return "Task (ADL) Audit";
      case "mar": return "MAR Audit";
      default: return "Compliance Audit";
    }
  };

  const getAuditMissingLabel = (type) => {
    switch (type) {
      case "notes": return "Missing Notes";
      case "tasks": return "Missing Tasks";
      case "mar": return "Missed MAR Entries";
      default: return "Missing Items";
    }
  };

  const handleToggleDefaultRule = async (field, currentValue) => {
    setTogglingRule(field);
    try {
      await updateComplianceSettings({ [field]: !currentValue });
      await getComplianceDashboard();
    } finally {
      setTogglingRule(null);
    }
  };

  const compliance = useSelector((state) => state.compliance) || {};
  const dashboard = compliance.dashboard || null;
  const loading = compliance.loading || false;
  const addLoading = compliance.addLoading || false;
  const settings = compliance.settings || null;
  const credentialTypes = compliance.credentialTypes || [];
  const clientItems = compliance.clientItems || [];
  const facilityItems = compliance.facilityItems || [];
  const alertsCount = compliance.alertsCount || 0;
  const staffOverview = compliance.staffOverview || [];
  const staffOverviewCount = compliance.staffOverviewCount || 0;
  const facilityOverview = compliance.facilityOverview || [];
  const facilityOverviewCount = compliance.facilityOverviewCount || 0;
  const clientDocOverview = compliance.clientDocOverview || [];
  const clientDocOverviewCount = compliance.clientDocOverviewCount || 0;
  const alerts = compliance.alerts || [];

  const facilityState = useSelector((state) => state.facility) || {};
  const rawFacilities = facilityState.allFacilities;
  const facilities = Array.isArray(rawFacilities) ? rawFacilities : (rawFacilities?.facilities || []);
  const noteState = useSelector((state) => state.note) || {};
  const rawNoteTypes = noteState.allNotesTypes;
  const noteTypes = Array.isArray(rawNoteTypes) ? rawNoteTypes : (rawNoteTypes?.ntypes || []);
  const taskState = useSelector((state) => state.task) || {};
  const taskTypes = taskState.taskTypes || [];
  const { profile } = useSelector((state) => state.profile) || {};

  useEffect(() => {
    getComplianceDashboard();
    getAllFacilitiesModal("all");
    getAllNoteTypes(false);
    getTaskTypes("all", false);
    getCredentialTypes();
    getClientComplianceItems();
    getFacilityComplianceItems();
    getComplianceSettings();
  }, []);

  useEffect(() => {
    if (activeTab === "regulatory") getStaffCredentialsOverview(currentPage);
    if (activeTab === "alerts") getComplianceAlerts(currentPage);
  }, [activeTab, currentPage]);

  useEffect(() => {
    if (activeTab === "regulatory") getFacilityDocumentsOverview(facilityDocPage, facilityDocSearch);
  }, [activeTab, facilityDocPage, facilityDocSearch]);

  useEffect(() => {
    if (activeTab === "regulatory") getClientDocumentsOverview(clientDocPage, clientDocSearch);
  }, [activeTab, clientDocPage, clientDocSearch]);

  useEffect(() => {
    if (settings && !settingsForm) {
      setSettingsForm({
        credentialExpiryAlertDays: settings.credentialExpiryAlertDays ?? 30,
        clientComplianceAlertDays: settings.clientComplianceAlertDays ?? 30,
        facilityComplianceAlertDays: settings.facilityComplianceAlertDays ?? 30,
        notesThreshold: settings.notesThreshold ?? 95,
        marThreshold: settings.marThreshold ?? 95,
        taskThreshold: settings.taskThreshold ?? 90,
        credentialWeight: settings.credentialWeight ?? 40,
        notesWeight: settings.notesWeight ?? 30,
        marWeight: settings.marWeight ?? 20,
        taskWeight: settings.taskWeight ?? 10,
      });
    }
  }, [settings]);

  const handleSettingsChange = (field, value) => {
    setSettingsForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    if (!settingsForm) return;
    const totalWeight = (settingsForm.credentialWeight || 0) + (settingsForm.notesWeight || 0) +
      (settingsForm.marWeight || 0) + (settingsForm.taskWeight || 0);
    if (totalWeight !== 100) {
      const swal = (await import("sweetalert")).default;
      swal({ title: "Score weights must add up to 100%", text: `Current total: ${totalWeight}%`, icon: "warning" });
      return;
    }
    setSavingSettings(true);
    await updateComplianceSettings(settingsForm);
    setSavingSettings(false);
  };

  const handleResetSettings = () => {
    if (settings) {
      setSettingsForm({
        credentialExpiryAlertDays: settings.credentialExpiryAlertDays ?? 30,
        clientComplianceAlertDays: settings.clientComplianceAlertDays ?? 30,
        facilityComplianceAlertDays: settings.facilityComplianceAlertDays ?? 30,
        notesThreshold: settings.notesThreshold ?? 95,
        marThreshold: settings.marThreshold ?? 95,
        taskThreshold: settings.taskThreshold ?? 90,
        credentialWeight: settings.credentialWeight ?? 40,
        notesWeight: settings.notesWeight ?? 30,
        marWeight: settings.marWeight ?? 20,
        taskWeight: settings.taskWeight ?? 10,
      });
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm("");
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleExpandStaff = async (staffId, activeTemplateNames) => {
    if (expandedStaff === staffId) { setExpandedStaff(null); return; }
    setExpandedStaff(staffId);
    setExpandedStaffLoading(true);
    try {
      const res = await https().get(`/document/file/get?staff=${staffId}&complianceFilter=true`);
      const files = res?.data?.activeFiles || [];
      const expired = res?.data?.expiredFiles || [];
      const hasActiveList = Array.isArray(activeTemplateNames) && activeTemplateNames.length > 0;
      const activeSet = hasActiveList ? new Set(activeTemplateNames) : null;
      const allFiles = [...files, ...expired].filter(f => f.isTemplate && (!activeSet || activeSet.has(f.name) || activeSet.has(f.fileDescription)));
      setExpandedStaffDocs(allFiles);
    } catch { setExpandedStaffDocs([]); }
    setExpandedStaffLoading(false);
  };

  const handleExpandFacility = async (facilityId, activeTemplateNames) => {
    if (expandedFacility === facilityId) { setExpandedFacility(null); return; }
    setExpandedFacility(facilityId);
    setExpandedFacilityLoading(true);
    try {
      const res = await https().get(`/document/file/get?facility=${facilityId}&complianceFilter=true`);
      const files = res?.data?.activeFiles || [];
      const expired = res?.data?.expiredFiles || [];
      const hasActiveList = Array.isArray(activeTemplateNames) && activeTemplateNames.length > 0;
      const activeSet = hasActiveList ? new Set(activeTemplateNames) : null;
      const allFiles = [...files, ...expired].filter(f => f.isTemplate && (!activeSet || activeSet.has(f.name) || activeSet.has(f.fileDescription)));
      setExpandedFacilityDocs(allFiles);
    } catch { setExpandedFacilityDocs([]); }
    setExpandedFacilityLoading(false);
  };

  const handleExpandClient = async (clientId, activeTemplateNames) => {
    if (expandedClient === clientId) { setExpandedClient(null); return; }
    setExpandedClient(clientId);
    setExpandedClientLoading(true);
    try {
      const res = await https().get(`/document/file/get?client=${clientId}&complianceFilter=true`);
      const files = res?.data?.activeFiles || [];
      const expired = res?.data?.expiredFiles || [];
      const hasActiveList = Array.isArray(activeTemplateNames) && activeTemplateNames.length > 0;
      const activeSet = hasActiveList ? new Set(activeTemplateNames) : null;
      const allFiles = [...files, ...expired].filter(f => f.isTemplate && (!activeSet || activeSet.has(f.name) || activeSet.has(f.fileDescription)));
      setExpandedClientDocs(allFiles);
    } catch { setExpandedClientDocs([]); }
    setExpandedClientLoading(false);
  };

  const getDocStatus = (doc) => {
    const hasFile = doc?.fileUrl && doc.fileUrl !== "";
    if (!hasFile) return "empty";
    if (doc?.expired) return "expired";
    if (doc?.expiry) {
      const exp = new Date(doc.expiry);
      const thirtyDays = new Date();
      thirtyDays.setDate(thirtyDays.getDate() + 30);
      if (exp <= thirtyDays && exp >= new Date()) return "expiring";
    }
    return "uploaded";
  };

  // --- Tab content renderers ---

  const getScoreColor = (score) => {
    if (score >= 80) return "#10B981";
    if (score >= 60) return "#F59E0B";
    return "#EF4444";
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical": return "red";
      case "high": return "orange";
      case "medium": return "yellow";
      default: return "gray";
    }
  };

  const getFacilityDotColor = (status) => {
    switch (status) {
      case "good": return "#10B981";
      case "warning": return "#F59E0B";
      case "critical": return "#EF4444";
      default: return "#9CA3AF";
    }
  };

  const renderOverview = () => {
    const overall = dashboard?.overallScore ?? 0;
    const reg = dashboard?.regulatory ?? {};
    const chart = dashboard?.charting ?? {};
    const ops = dashboard?.operational ?? {};
    const alertsList = dashboard?.alerts ?? [];
    const facilityHealthList = dashboard?.facilityHealth ?? [];
    const summary = dashboard?.summary ?? {};

    return (
      <Box>
        {loading ? (
          <Flex align="center" justify="center" py={60}><Loader color="#FFAF4A" /></Flex>
        ) : (
          <>
            {/* ── Row 1: Overall Score + 3 Sub-Scores ── */}
            <Grid mb={20} gutter={16}>
              {/* Overall Score Ring */}
              <Grid.Col span={3}>
                <Paper className={classes.overallScoreCard} style={{ height: "100%" }}>
                  <Text ff="Inter" fz={12} fw={600} color="rgba(255,255,255,0.6)" mb={12}
                    style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Overall Compliance
                  </Text>
                  <Flex align="center" gap={20}>
                    <RingProgress
                      size={120}
                      thickness={10}
                      roundCaps
                      sections={[{ value: overall, color: getScoreColor(overall) }]}
                      label={
                        <Text align="center" ff="Inter" fw={800} fz={28} color="#FFF">
                          {overall}
                        </Text>
                      }
                    />
                    <Box>
                      <Text ff="Inter" fz={38} fw={800} color="#FFF" lh={1}>{overall}</Text>
                      <Text ff="Inter" fz={12} fw={500} color="rgba(255,255,255,0.5)" mt={2}>out of 100</Text>
                    </Box>
                  </Flex>
                </Paper>
              </Grid.Col>

              {/* Regulatory Compliance (Documents) */}
              <Grid.Col span={3}>
                <Paper className={classes.dashCard} style={{ height: "100%" }}>
                  <Group spacing={8} mb={8}>
                    <ThemeIcon size={32} radius="md" variant="light" color="blue">
                      <IconShieldCheck size={18} />
                    </ThemeIcon>
                    <Text className={classes.scoreCardTitle} mb={0}>Regulatory</Text>
                  </Group>
                  <Text className={classes.scoreCardValue} color={getScoreColor(reg.score ?? 0)}>
                    {reg.score ?? 0}%
                  </Text>
                  <Text className={classes.scoreCardSub}>
                    {(reg.staffDocs?.expired || 0) + (reg.facilityDocs?.expired || 0)} expired documents
                  </Text>
                  <Box className={classes.scoreBar}>
                    <Box style={{
                      height: "100%",
                      width: `${reg.score ?? 0}%`,
                      borderRadius: 4,
                      background: `linear-gradient(90deg, ${getScoreColor(reg.score ?? 0)}, ${getScoreColor(reg.score ?? 0)}88)`,
                      transition: "width 0.6s ease",
                    }} />
                  </Box>
                  <Box mt={10}>
                    <Group spacing={12}>
                      <Text fz={11} ff="Inter" color="#6B7280">Staff Docs: {reg.staffDocs?.score ?? 0}%</Text>
                      <Text fz={11} ff="Inter" color="#6B7280">Facility Docs: {reg.facilityDocs?.score ?? 0}%</Text>
                      <Text fz={11} ff="Inter" color="#6B7280">Client Docs: {reg.clientDocs?.score ?? 0}%</Text>
                    </Group>
                    {(reg.clientDocs?.carePlan?.missing || 0) > 0 && (
                      <Text fz={11} ff="Inter" color="#EF4444" mt={4}>
                        {reg.clientDocs.carePlan.missing} client{reg.clientDocs.carePlan.missing > 1 ? "s" : ""} missing active care plan
                      </Text>
                    )}
                  </Box>
                </Paper>
              </Grid.Col>

              {/* Charting Compliance */}
              <Grid.Col span={3}>
                <Paper className={classes.dashCard} style={{ height: "100%" }}>
                  <Group spacing={8} mb={8}>
                    <ThemeIcon size={32} radius="md" variant="light" color="grape">
                      <IconClipboardCheck size={18} />
                    </ThemeIcon>
                    <Text className={classes.scoreCardTitle} mb={0}>Charting</Text>
                  </Group>
                  <Text className={classes.scoreCardValue} color={getScoreColor(chart.score ?? 0)}>
                    {chart.score ?? 0}%
                  </Text>
                  <Text className={classes.scoreCardSub}>
                    {(chart.notes?.incomplete || 0) + (chart.tasks?.incomplete || 0) + (chart.mar?.incomplete || 0)} incomplete items
                  </Text>
                  <Box className={classes.scoreBar}>
                    <Box style={{
                      height: "100%",
                      width: `${chart.score ?? 0}%`,
                      borderRadius: 4,
                      background: `linear-gradient(90deg, ${getScoreColor(chart.score ?? 0)}, ${getScoreColor(chart.score ?? 0)}88)`,
                      transition: "width 0.6s ease",
                    }} />
                  </Box>
                  <Group spacing={12} mt={10}>
                    <Text fz={11} ff="Inter" color="#6B7280">Notes: {chart.notes?.score ?? 0}%</Text>
                    <Text fz={11} ff="Inter" color="#6B7280">Tasks: {chart.tasks?.score ?? 0}%</Text>
                    <Text fz={11} ff="Inter" color="#6B7280">MAR: {chart.mar?.score ?? 0}%</Text>
                  </Group>
                </Paper>
              </Grid.Col>

              {/* Operational Compliance */}
              <Grid.Col span={3}>
                <Paper className={classes.dashCard} style={{ height: "100%" }}>
                  <Group spacing={8} mb={8}>
                    <ThemeIcon size={32} radius="md" variant="light" color="teal">
                      <IconCheckupList size={18} />
                    </ThemeIcon>
                    <Text className={classes.scoreCardTitle} mb={0}>Operational</Text>
                  </Group>
                  <Text className={classes.scoreCardValue} color={getScoreColor(ops.score ?? 0)}>
                    {ops.score ?? 0}%
                  </Text>
                  <Text className={classes.scoreCardSub}>
                    {ops.overdueTasks || 0} tasks overdue
                  </Text>
                  <Box className={classes.scoreBar}>
                    <Box style={{
                      height: "100%",
                      width: `${ops.score ?? 0}%`,
                      borderRadius: 4,
                      background: `linear-gradient(90deg, ${getScoreColor(ops.score ?? 0)}, ${getScoreColor(ops.score ?? 0)}88)`,
                      transition: "width 0.6s ease",
                    }} />
                  </Box>
                  <Group spacing={12} mt={10}>
                    <Text fz={11} ff="Inter" color="#6B7280">{ops.completedTasks ?? 0}/{ops.totalTasks ?? 0} completed</Text>
                    <Text fz={11} ff="Inter" color="#6B7280">{ops.overdueTasks ?? 0} overdue</Text>
                  </Group>
                </Paper>
              </Grid.Col>
            </Grid>

            {/* ── Row 2: Alerts + Facility Health ── */}
            <Grid mb={20} gutter={16}>
              {/* Compliance Alerts */}
              <Grid.Col span={8}>
                <Paper className={classes.dashCard} style={{ height: "100%" }}>
                  <Group position="apart" mb={16}>
                    <Group spacing={8}>
                      <IconAlertTriangle size={18} color="#F59E0B" />
                      <Text ff="Inter" fw={600} fz={16} color="#1F2937">Compliance Alerts</Text>
                    </Group>
                    <Badge variant="light" color="gray" radius="md" size="sm" ff="Inter">
                      {alertsList.length} active
                    </Badge>
                  </Group>
                  <ScrollArea style={{ maxHeight: 320 }}>
                    {alertsList.length > 0 ? alertsList.map((alert) => (
                      <Box key={alert.id} className={classes.alertItem}>
                        <Flex justify="space-between" align="center">
                          <Box style={{ flex: 1 }}>
                            <Group spacing={8} mb={4}>
                              <Badge
                                className={classes.severityBadge}
                                color={getSeverityColor(alert.severity)}
                                variant="filled"
                                size="xs"
                                radius="sm"
                              >
                                {alert.severity}
                              </Badge>
                              <Badge variant="outline" color="gray" size="xs" radius="sm" ff="Inter" fw={500}>
                                {alert.type}
                              </Badge>
                            </Group>
                            <Text ff="Inter" fz={14} fw={500} color="#1F2937" lineClamp={1}>
                              {alert.title}
                            </Text>
                          </Box>
                          {alert.dueDate && (
                            <Group spacing={4} ml={12}>
                              <IconCalendar size={14} color="#9CA3AF" />
                              <Text ff="Inter" fz={12} color="#6B7280">
                                {moment(alert.dueDate).format("MMM DD, YYYY")}
                              </Text>
                            </Group>
                          )}
                        </Flex>
                      </Box>
                    )) : (
                      <Flex align="center" justify="center" py={40}>
                        <Text ff="Inter" fz={14} color="#9CA3AF">No active alerts</Text>
                      </Flex>
                    )}
                  </ScrollArea>
                </Paper>
              </Grid.Col>

              {/* Facility Health */}
              <Grid.Col span={4}>
                <Paper className={classes.dashCard} style={{ height: "100%" }}>
                  <Group spacing={8} mb={16}>
                    <IconBuildingHospital size={18} color="#6366F1" />
                    <Text ff="Inter" fw={600} fz={16} color="#1F2937">Facility Health</Text>
                  </Group>
                  <ScrollArea style={{ maxHeight: 320 }}>
                    {facilityHealthList.length > 0 ? facilityHealthList.map((fh) => (
                      <Box key={fh.id} className={classes.facilityBar}>
                        <Box style={{
                          width: 10, height: 10, borderRadius: "50%",
                          backgroundColor: getFacilityDotColor(fh.status),
                          flexShrink: 0,
                        }} />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text ff="Inter" fz={13} fw={500} color="#374151" lineClamp={1}>{fh.name}</Text>
                          <Progress
                            value={fh.score}
                            color={fh.status === "good" ? "green" : fh.status === "warning" ? "yellow" : "red"}
                            size="xs"
                            radius="xl"
                            mt={4}
                          />
                        </Box>
                        <Text ff="Inter" fz={14} fw={700} color="#374151" style={{ minWidth: 32, textAlign: "right" }}>
                          {fh.score}
                        </Text>
                      </Box>
                    )) : (
                      <Flex align="center" justify="center" py={40}>
                        <Text ff="Inter" fz={14} color="#9CA3AF">No facilities configured</Text>
                      </Flex>
                    )}
                  </ScrollArea>
                </Paper>
              </Grid.Col>
            </Grid>

            {/* ── Row 3: Summary Stats ── */}
            <SimpleGrid cols={3} mb={20}>
              <Paper className={classes.summaryStatCard}>
                <Text ff="Inter" fz={32} fw={700} color="#EF4444">{summary.activeAlerts || 0}</Text>
                <Text ff="Inter" fz={13} fw={500} color="#6B7280" mt={4}>Active Regulatory Alerts</Text>
              </Paper>
              <Paper className={classes.summaryStatCard}>
                <Text ff="Inter" fz={32} fw={700} color="#F59E0B">{summary.pendingActions || 0}</Text>
                <Text ff="Inter" fz={13} fw={500} color="#6B7280" mt={4}>Pending Actions</Text>
              </Paper>
              <Paper className={classes.summaryStatCard}>
                <Text ff="Inter" fz={32} fw={700} color="#10B981">{summary.totalClients || 0}</Text>
                <Text ff="Inter" fz={13} fw={500} color="#6B7280" mt={4}>Clients Monitored</Text>
              </Paper>
            </SimpleGrid>
          </>
        )}
      </Box>
    );
  };

  const categoryColor = (cat) => {
    switch (cat) {
      case "document": return "violet";
      case "notes": return "blue";
      case "task": return "teal";
      case "mar": return "orange";
      case "license": return "indigo";
      case "inspection": return "cyan";
      default: return "gray";
    }
  };

  // ── Filtered lists per tab ──
  const regulatoryClientItems = clientItems.filter((ci) => ci.category === "document");
  const chartingItems = clientItems.filter((ci) => ["notes", "task", "mar"].includes(ci.category));

  const filteredFacilityItems = facilityItems.filter((fi) => {
    if (!facilitySearch.trim()) return true;
    const term = facilitySearch.toLowerCase();
    return (
      (fi.name || "").toLowerCase().includes(term) ||
      (fi.category || "").toLowerCase().includes(term) ||
      (fi.facility?.name || "").toLowerCase().includes(term)
    );
  });

  const filteredChartingItems = chartingItems.filter((ci) => {
    if (!clientSearch.trim()) return true;
    const term = clientSearch.toLowerCase();
    return (
      (ci.name || "").toLowerCase().includes(term) ||
      (ci.category || "").toLowerCase().includes(term)
    );
  });

  // ═══════════════════════════════════════════════
  //  REGULATORY TAB (Staff credentials + Facility docs + Client docs)
  // ═══════════════════════════════════════════════
  const renderRegulatory = () => (
    <Box>
      {/* ── Section 1: Staff Credentials ── */}
      <Text className={classes.sectionTitle}>Staff Documents</Text>
      {credentialTypes.length > 0 && (
        <Paper withBorder radius="md" mb={12} p="sm">
          <Text fz={13} fw={600} ff="Inter" mb={8} color="#737373">Required Credential Types</Text>
          <Group spacing={8}>
            {credentialTypes.map((ct) => (
              <Badge key={ct._id || ct.id} variant="outline" color="blue" size="md" radius="md">{ct.name}</Badge>
            ))}
          </Group>
        </Paper>
      )}
      <Box className={classes.table}>
        <Box className={classes.search}>
          <Input
            icon={<IconSearch />}
            placeholder="Search staff name"
            className={classes.searchInput}
            radius="md"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
        <ScrollArea>
          <Table withBorder fontSize="sm" verticalSpacing="md" highlightOnHover>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Staff Name</th>
                <th style={{ minWidth: 150 }}>Position</th>
                <th style={{ minWidth: 100 }}>Score</th>
                <th style={{ minWidth: 120 }}>Status</th>
                <th style={{ minWidth: 120 }}>Documents</th>
                <th style={{ minWidth: 100 }}>Compliant</th>
                <th style={{ minWidth: 100 }}>Expiring</th>
                <th style={{ minWidth: 100 }}>Expired</th>
                <th style={{ minWidth: 100 }}>Missing</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
              ) : staffOverview.length > 0 ? (
                staffOverview.map((s, i) => (
                  <React.Fragment key={s._id || i}>
                    <tr onClick={() => handleExpandStaff(s._id, s.activeTemplateNames)} style={{ cursor: "pointer" }}>
                      <td>
                        <Group spacing={6}>
                          {expandedStaff === s._id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                          <Text fz={14}>{s.firstName} {s.lastName}</Text>
                        </Group>
                      </td>
                      <td>{s.position || "\u2014"}</td>
                      <td>
                        <Group spacing={8}>
                          <Progress value={s.credentialSummary?.score || 0} color={s.credentialSummary?.score >= 80 ? "green" : s.credentialSummary?.score >= 50 ? "yellow" : "red"} size="sm" style={{ width: 60 }} />
                          <Text fz={13} fw={500}>{s.credentialSummary?.score || 0}%</Text>
                        </Group>
                      </td>
                      <td><Badge color={statusColor(s.credentialSummary?.overallStatus)} className={classes.statusBadge}>{(s.credentialSummary?.overallStatus || "").replace("_", " ")}</Badge></td>
                      <td><Text fz={13}>{s.documentSummary?.uploaded || 0}/{s.documentSummary?.total || 0}</Text></td>
                      <td>{s.credentialSummary?.compliant || 0}</td>
                      <td>{s.credentialSummary?.expiring || 0}</td>
                      <td>{s.credentialSummary?.expired || 0}</td>
                      <td>{s.credentialSummary?.missing || 0}</td>
                    </tr>
                    {expandedStaff === s._id && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, backgroundColor: "#F9FAFB" }}>
                          <Box p="sm">
                            <Text fz={13} fw={600} ff="Inter" mb={8}>Documents for {s.firstName} {s.lastName}</Text>
                            {expandedStaffLoading ? (
                              <Flex justify="center" py={16}><Loader color="#FFAF4A" size="xs" /></Flex>
                            ) : expandedStaffDocs.length > 0 ? (
                              <Table fontSize="xs" verticalSpacing="xs">
                                <thead><tr><th>Document Name</th><th>Description</th><th>Expiry</th><th>Status</th></tr></thead>
                                <tbody>
                                  {expandedStaffDocs.map((doc) => {
                                    const st = getDocStatus(doc);
                                    return (
                                      <tr key={doc._id || doc.id}>
                                        <td><Text fz={12}>{doc.name}</Text></td>
                                        <td><Text fz={12} color="dimmed">{doc.fileDescription || "\u2014"}</Text></td>
                                        <td><Text fz={12}>{doc.expiry ? moment(doc.expiry).format("MMM DD, YYYY") : "\u2014"}</Text></td>
                                        <td><Badge size="xs" color={st === "uploaded" ? "green" : st === "expiring" ? "yellow" : st === "expired" ? "red" : "gray"}>{st}</Badge></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            ) : (
                              <Text fz={12} color="dimmed">No documents found</Text>
                            )}
                          </Box>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr><td colSpan={9}><RecordNotFound /></td></tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
        <CustomBillingPagination
          data={staffOverviewCount}
          itemsPerPage={10}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      </Box>

      {/* ── Section 2: Facility Documents ── */}
      <Text className={classes.sectionTitle} mt={24}>Facility Documents</Text>
      <Box className={classes.table}>
        <Box className={classes.search}>
          <Input
            icon={<IconSearch />}
            placeholder="Search facility name"
            className={classes.searchInput}
            radius="md"
            value={facilityDocSearch}
            onChange={(e) => setFacilityDocSearch(e.target.value)}
          />
        </Box>
        <ScrollArea>
          <Table withBorder fontSize="sm" verticalSpacing="md" highlightOnHover>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Facility Name</th>
                <th style={{ minWidth: 100 }}>Score</th>
                <th style={{ minWidth: 120 }}>Status</th>
                <th style={{ minWidth: 120 }}>Documents</th>
                <th style={{ minWidth: 100 }}>Compliant</th>
                <th style={{ minWidth: 100 }}>Expiring</th>
                <th style={{ minWidth: 100 }}>Expired</th>
                <th style={{ minWidth: 100 }}>Missing</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
              ) : facilityOverview.length > 0 ? (
                facilityOverview.map((f, i) => (
                  <React.Fragment key={f._id || i}>
                    <tr onClick={() => handleExpandFacility(f._id, f.activeTemplateNames)} style={{ cursor: "pointer" }}>
                      <td>
                        <Group spacing={6}>
                          {expandedFacility === f._id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                          <Text fz={14} fw={500} ff="Inter">{f.name}</Text>
                        </Group>
                      </td>
                      <td>
                        <Group spacing={8}>
                          <Progress value={f.documentSummary?.score || 0} color={f.documentSummary?.score >= 80 ? "green" : f.documentSummary?.score >= 50 ? "yellow" : "red"} size="sm" style={{ width: 60 }} />
                          <Text fz={13} fw={500}>{f.documentSummary?.score || 0}%</Text>
                        </Group>
                      </td>
                      <td><Badge color={statusColor(f.documentSummary?.overallStatus)} className={classes.statusBadge}>{(f.documentSummary?.overallStatus || "").replace("_", " ")}</Badge></td>
                      <td><Text fz={13}>{f.documentSummary?.uploaded || 0}/{f.documentSummary?.total || 0}</Text></td>
                      <td>{f.documentSummary?.compliant || 0}</td>
                      <td>{f.documentSummary?.expiring || 0}</td>
                      <td>{f.documentSummary?.expired || 0}</td>
                      <td>{f.documentSummary?.missing || 0}</td>
                    </tr>
                    {expandedFacility === f._id && (
                      <tr>
                        <td colSpan={8} style={{ padding: 0, backgroundColor: "#F9FAFB" }}>
                          <Box p="sm">
                            <Text fz={13} fw={600} ff="Inter" mb={8}>Documents for {f.name}</Text>
                            {expandedFacilityLoading ? (
                              <Flex justify="center" py={16}><Loader color="#FFAF4A" size="xs" /></Flex>
                            ) : expandedFacilityDocs.length > 0 ? (
                              <Table fontSize="xs" verticalSpacing="xs">
                                <thead><tr><th>Document Name</th><th>Description</th><th>Expiry</th><th>Status</th></tr></thead>
                                <tbody>
                                  {expandedFacilityDocs.map((doc) => {
                                    const st = getDocStatus(doc);
                                    return (
                                      <tr key={doc._id || doc.id}>
                                        <td><Text fz={12}>{doc.name}</Text></td>
                                        <td><Text fz={12} color="dimmed">{doc.fileDescription || "\u2014"}</Text></td>
                                        <td><Text fz={12}>{doc.expiry ? moment(doc.expiry).format("MMM DD, YYYY") : "\u2014"}</Text></td>
                                        <td><Badge size="xs" color={st === "uploaded" ? "green" : st === "expiring" ? "yellow" : st === "expired" ? "red" : "gray"}>{st}</Badge></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            ) : (
                              <Text fz={12} color="dimmed">No documents found</Text>
                            )}
                          </Box>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr><td colSpan={8}><RecordNotFound /></td></tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
        <CustomBillingPagination
          data={facilityOverviewCount}
          itemsPerPage={10}
          currentPage={facilityDocPage}
          handlePageChange={(page) => setFacilityDocPage(page)}
        />
      </Box>

      {/* ── Section 3: Client Documents ── */}
      <Text className={classes.sectionTitle} mt={24}>Client Documents</Text>
      <Box className={classes.table}>
        <Box className={classes.search}>
          <Input
            icon={<IconSearch />}
            placeholder="Search client name"
            className={classes.searchInput}
            radius="md"
            value={clientDocSearch}
            onChange={(e) => setClientDocSearch(e.target.value)}
          />
        </Box>
        <ScrollArea>
          <Table withBorder fontSize="sm" verticalSpacing="md" highlightOnHover>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Client Name</th>
                <th style={{ minWidth: 100 }}>Score</th>
                <th style={{ minWidth: 120 }}>Status</th>
                <th style={{ minWidth: 120 }}>Documents</th>
                <th style={{ minWidth: 100 }}>Compliant</th>
                <th style={{ minWidth: 100 }}>Expiring</th>
                <th style={{ minWidth: 100 }}>Expired</th>
                <th style={{ minWidth: 100 }}>Missing</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
              ) : clientDocOverview.length > 0 ? (
                clientDocOverview.map((c, i) => (
                  <React.Fragment key={c._id || i}>
                    <tr onClick={() => handleExpandClient(c._id, c.activeTemplateNames)} style={{ cursor: "pointer" }}>
                      <td>
                        <Group spacing={6}>
                          {expandedClient === c._id ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                          <Text fz={14} fw={500} ff="Inter">{c.clientName || (c.form1?.firstName ? `${c.form1.firstName} ${c.form1.lastName}` : "Unknown")}</Text>
                        </Group>
                      </td>
                      <td>
                        <Group spacing={8}>
                          <Progress value={c.documentSummary?.score || 0} color={c.documentSummary?.score >= 80 ? "green" : c.documentSummary?.score >= 50 ? "yellow" : "red"} size="sm" style={{ width: 60 }} />
                          <Text fz={13} fw={500}>{c.documentSummary?.score || 0}%</Text>
                        </Group>
                      </td>
                      <td><Badge color={statusColor(c.documentSummary?.overallStatus)} className={classes.statusBadge}>{(c.documentSummary?.overallStatus || "").replace("_", " ")}</Badge></td>
                      <td><Text fz={13}>{c.documentSummary?.uploaded || 0}/{c.documentSummary?.total || 0}</Text></td>
                      <td>{c.documentSummary?.compliant || 0}</td>
                      <td>{c.documentSummary?.expiring || 0}</td>
                      <td>{c.documentSummary?.expired || 0}</td>
                      <td>{c.documentSummary?.missing || 0}</td>
                    </tr>
                    {expandedClient === c._id && (
                      <tr>
                        <td colSpan={8} style={{ padding: 0, backgroundColor: "#F9FAFB" }}>
                          <Box p="sm">
                            <Text fz={13} fw={600} ff="Inter" mb={8}>Documents for {c.clientName || (c.form1?.firstName ? `${c.form1.firstName} ${c.form1.lastName}` : "Unknown")}</Text>
                            {expandedClientLoading ? (
                              <Flex justify="center" py={16}><Loader color="#FFAF4A" size="xs" /></Flex>
                            ) : expandedClientDocs.length > 0 ? (
                              <Table fontSize="xs" verticalSpacing="xs">
                                <thead><tr><th>Document Name</th><th>Description</th><th>Expiry</th><th>Status</th></tr></thead>
                                <tbody>
                                  {expandedClientDocs.map((doc) => {
                                    const st = getDocStatus(doc);
                                    return (
                                      <tr key={doc._id || doc.id}>
                                        <td><Text fz={12}>{doc.name}</Text></td>
                                        <td><Text fz={12} color="dimmed">{doc.fileDescription || "\u2014"}</Text></td>
                                        <td><Text fz={12}>{doc.expiry ? moment(doc.expiry).format("MMM DD, YYYY") : "\u2014"}</Text></td>
                                        <td><Badge size="xs" color={st === "uploaded" ? "green" : st === "expiring" ? "yellow" : st === "expired" ? "red" : "gray"}>{st}</Badge></td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            ) : (
                              <Text fz={12} color="dimmed">No documents found</Text>
                            )}
                          </Box>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr><td colSpan={8}><RecordNotFound /></td></tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
        <CustomBillingPagination
          data={clientDocOverviewCount}
          itemsPerPage={10}
          currentPage={clientDocPage}
          handlePageChange={(page) => setClientDocPage(page)}
        />
      </Box>
    </Box>
  );

  // ═══════════════════════════════════════════════
  //  CHARTING TAB (Notes, MAR, Task — daily checks per client)
  // ═══════════════════════════════════════════════
  const renderCharting = () => {
    const chart = dashboard?.charting ?? {};
    return (
      <Box>
        <Text className={classes.title}>Charting Compliance Rules</Text>
        <Text fz={13} color="dimmed" ff="Inter" mb={12}>
          Checked daily for each client in every facility. Notes and Tasks require daily entries per client.
          MAR checks that every medication status is not &quot;Missed&quot;.
        </Text>

        {/* ── Default Compliance Items ── */}
        <Text className={classes.sectionTitle}>Default Rules</Text>
        <SimpleGrid cols={3} mb={20}>
          {/* Progress Note */}
          <Paper className={classes.dashCard} style={{ opacity: chart.notes?.active === false ? 0.5 : 1 }}>
            <Group position="apart" mb={8}>
              <Group spacing={8}>
                <Badge variant="light" color="green" size="xs" radius="sm">DEFAULT</Badge>
                <Badge variant="light" color="blue" size="xs" radius="sm">NOTES</Badge>
              </Group>
              <Switch
                size="sm"
                color="teal"
                checked={chart.notes?.active !== false}
                onChange={() => handleToggleDefaultRule("defaultNotesActive", chart.notes?.active !== false)}
                disabled={togglingRule === "defaultNotesActive"}
                label={<Text fz={11} ff="Inter" fw={500} color="#6B7280">{chart.notes?.active !== false ? "Active" : "Inactive"}</Text>}
                labelPosition="left"
                styles={{ label: { paddingRight: 6 } }}
              />
            </Group>
            <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={4}>Progress Note</Text>
            <Text fz={12} ff="Inter" color="#6B7280" mb={8}>Note Type: Progress Note</Text>
            <Group spacing={6} mb={6}>
              <Progress value={chart.notes?.active !== false ? (chart.notes?.score ?? 0) : 0} color={getScoreColor(chart.notes?.score ?? 0)} size="sm" style={{ flex: 1 }} radius="xl" />
              <Text fz={13} fw={600} ff="Inter" color={getScoreColor(chart.notes?.score ?? 0)}>{chart.notes?.active !== false ? (chart.notes?.score ?? 0) : 0}%</Text>
            </Group>
            <Text fz={11} ff="Inter" color="#6B7280">Threshold: 100%</Text>
            <Divider my={8} />
            <Group spacing={16}>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#EF4444">{chart.notes?.clientsMissing ?? 0}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Clients Missing Notes</Text>
              </Box>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#F59E0B">{chart.notes?.incomplete ?? 0}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Missing Entries</Text>
              </Box>
            </Group>
            {!chart.notes?.noteTypeFound && (
              <Text fz={11} ff="Inter" color="#EF4444" mt={6}>Progress Note type not found in system</Text>
            )}
            <Flex justify="flex-end" mt={12}>
              <Button size="xs" variant="light" color="blue" radius="md" ff="Inter"
                onClick={() => handleRunAudit("notes")}>
                Run Audit
              </Button>
            </Flex>
          </Paper>

          {/* Task — Activities of Daily Living */}
          <Paper className={classes.dashCard} style={{ opacity: chart.tasks?.active === false ? 0.5 : 1 }}>
            <Group position="apart" mb={8}>
              <Group spacing={8}>
                <Badge variant="light" color="green" size="xs" radius="sm">DEFAULT</Badge>
                <Badge variant="light" color="teal" size="xs" radius="sm">TASK</Badge>
              </Group>
              <Switch
                size="sm"
                color="teal"
                checked={chart.tasks?.active !== false}
                onChange={() => handleToggleDefaultRule("defaultTaskActive", chart.tasks?.active !== false)}
                disabled={togglingRule === "defaultTaskActive"}
                label={<Text fz={11} ff="Inter" fw={500} color="#6B7280">{chart.tasks?.active !== false ? "Active" : "Inactive"}</Text>}
                labelPosition="left"
                styles={{ label: { paddingRight: 6 } }}
              />
            </Group>
            <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={4}>Task</Text>
            <Text fz={12} ff="Inter" color="#6B7280" mb={8}>Task Type: Activities of Daily Living</Text>
            <Group spacing={6} mb={6}>
              <Progress value={chart.tasks?.active !== false ? (chart.tasks?.score ?? 0) : 0} color={getScoreColor(chart.tasks?.score ?? 0)} size="sm" style={{ flex: 1 }} radius="xl" />
              <Text fz={13} fw={600} ff="Inter" color={getScoreColor(chart.tasks?.score ?? 0)}>{chart.tasks?.active !== false ? (chart.tasks?.score ?? 0) : 0}%</Text>
            </Group>
            <Text fz={11} ff="Inter" color="#6B7280">Threshold: 100%</Text>
            <Divider my={8} />
            <Group spacing={16}>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#EF4444">{chart.tasks?.clientsMissing ?? 0}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Clients Missing Tasks</Text>
              </Box>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#F59E0B">{chart.tasks?.incomplete ?? 0}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Missing Entries</Text>
              </Box>
            </Group>
            {!chart.tasks?.taskTypeFound && (
              <Text fz={11} ff="Inter" color="#EF4444" mt={6}>Activities of Daily Living task type not found</Text>
            )}
            <Flex justify="flex-end" mt={12}>
              <Button size="xs" variant="light" color="blue" radius="md" ff="Inter"
                onClick={() => handleRunAudit("tasks")}>
                Run Audit
              </Button>
            </Flex>
          </Paper>

          {/* MAR */}
          <Paper className={classes.dashCard} style={{ opacity: chart.mar?.active === false ? 0.5 : 1 }}>
            <Group position="apart" mb={8}>
              <Group spacing={8}>
                <Badge variant="light" color="green" size="xs" radius="sm">DEFAULT</Badge>
                <Badge variant="light" color="orange" size="xs" radius="sm">MAR</Badge>
              </Group>
              <Switch
                size="sm"
                color="teal"
                checked={chart.mar?.active !== false}
                onChange={() => handleToggleDefaultRule("defaultMarActive", chart.mar?.active !== false)}
                disabled={togglingRule === "defaultMarActive"}
                label={<Text fz={11} ff="Inter" fw={500} color="#6B7280">{chart.mar?.active !== false ? "Active" : "Inactive"}</Text>}
                labelPosition="left"
                styles={{ label: { paddingRight: 6 } }}
              />
            </Group>
            <Text ff="Inter" fz={15} fw={600} color="#1F2937" mb={4}>MAR</Text>
            <Text fz={12} ff="Inter" color="#6B7280" mb={8}>Every status except &quot;Missed&quot; is compliant</Text>
            <Group spacing={6} mb={6}>
              <Progress value={chart.mar?.active !== false ? (chart.mar?.score ?? 0) : 0} color={getScoreColor(chart.mar?.score ?? 0)} size="sm" style={{ flex: 1 }} radius="xl" />
              <Text fz={13} fw={600} ff="Inter" color={getScoreColor(chart.mar?.score ?? 0)}>{chart.mar?.active !== false ? (chart.mar?.score ?? 0) : 0}%</Text>
            </Group>
            <Text fz={11} ff="Inter" color="#6B7280">Threshold: 100%</Text>
            <Divider my={8} />
            <Group spacing={16}>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#EF4444">{chart.mar?.clientsMissing ?? 0}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Clients Missing MAR</Text>
              </Box>
              <Box>
                <Text fz={18} fw={700} ff="Inter" color="#F59E0B">{chart.mar?.incomplete ?? 0}</Text>
                <Text fz={11} ff="Inter" color="#6B7280">Missed Entries</Text>
              </Box>
            </Group>
            <Flex justify="flex-end" mt={12}>
              <Button size="xs" variant="light" color="blue" radius="md" ff="Inter"
                onClick={() => handleRunAudit("mar")}>
                Run Audit
              </Button>
            </Flex>
          </Paper>
        </SimpleGrid>

        {/* ── Additional User-Created Charting Rules ── */}
        {filteredChartingItems.length > 0 && (
          <>
            <Text className={classes.sectionTitle}>Additional Rules</Text>
            <Box className={classes.table}>
              <Box className={classes.search}>
                <Input
                  icon={<IconSearch />}
                  placeholder="Search by name or category"
                  className={classes.searchInput}
                  radius="md"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
              </Box>
              <ScrollArea>
                <Table withBorder fontSize="sm" verticalSpacing="md" highlightOnHover>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 200 }}>Name</th>
                      <th style={{ minWidth: 110 }}>Type</th>
                      <th style={{ minWidth: 160 }}>Compliance Rule</th>
                      <th style={{ minWidth: 120, textAlign: "center" }}>Clients Missing</th>
                      <th style={{ minWidth: 120, textAlign: "center" }}>Missing Entries</th>
                      <th style={{ minWidth: 140 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChartingItems.map((ci) => {
                      const auditKey = ci.category === "task" ? "tasks" : ci.category;
                      const ruleData = chart[ci.category === "task" ? "tasks" : ci.category];
                      return (
                        <tr key={ci._id || ci.id}>
                          <td>
                            <Text fz={14} fw={500} ff="Inter">{ci.name}</Text>
                            {ci.description && <Text fz={12} color="dimmed" lineClamp={1}>{ci.description}</Text>}
                            {ci.approvedAt ? (
                              <Text fz={11} ff="Inter" color="#10B981" mt={2}>
                                Last approved by Ogiso: {moment(ci.approvedAt).format("MMM DD, YYYY")}
                              </Text>
                            ) : (
                              <Text fz={11} ff="Inter" color="#9CA3AF" mt={2}>Not yet approved by Ogiso</Text>
                            )}
                          </td>
                          <td>
                            <Badge variant="light" color={categoryColor(ci.category)} size="sm" radius="md" tt="uppercase">
                              {ci.category}
                            </Badge>
                          </td>
                          <td>
                            <Box>
                              <Group spacing={6}>
                                <Progress value={ci.threshold || 95} color={ci.threshold >= 90 ? "green" : ci.threshold >= 70 ? "yellow" : "red"} size="sm" style={{ width: 60 }} />
                                <Text fz={13} fw={500} ff="Inter">{ci.threshold ?? 95}% threshold</Text>
                              </Group>
                              {ci.category === "notes" && ci.noteType?.name && (
                                <Text fz={12} color="dimmed" mt={2}>Note Type: {ci.noteType.name}</Text>
                              )}
                              {ci.category === "task" && ci.taskType?.name && (
                                <Text fz={12} color="dimmed" mt={2}>Task Type: {ci.taskType.name}</Text>
                              )}
                              {ci.category === "mar" && (
                                <Text fz={12} color="dimmed" mt={2}>Checks completed (not missed) MAR entries</Text>
                              )}
                            </Box>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Text fz={16} fw={700} ff="Inter" color="#EF4444">{ruleData?.clientsMissing ?? "—"}</Text>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Text fz={16} fw={700} ff="Inter" color="#F59E0B">{ruleData?.incomplete ?? "—"}</Text>
                          </td>
                          <td>
                            <Group spacing={4}>
                              <Button size="xs" variant="light" color="blue" radius="md" ff="Inter" compact
                                onClick={() => handleRunAudit(auditKey)}>
                                Run Audit
                              </Button>
                              <ActionIcon size="sm" variant="subtle" color="blue"
                                onClick={() => handleEditClientItem(ci)}>
                                <IconEdit size={16} />
                              </ActionIcon>
                              <ActionIcon size="sm" variant="subtle" color="red"
                                onClick={() => handleDeleteClientItem(ci)}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Group>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </ScrollArea>
            </Box>
          </>
        )}
      </Box>
    );
  };

  // ═══════════════════════════════════════════════
  //  OPERATIONAL TAB (Task completion rate)
  // ═══════════════════════════════════════════════
  const renderOperational = () => {
    const ops = dashboard?.operational ?? {};
    return (
      <Box>
        <Text className={classes.title}>Operational Compliance</Text>
        <Text fz={13} color="dimmed" ff="Inter" mb={16}>
          Tracks assigned task completion rates across all clients and staff.
        </Text>

        {/* Stats cards */}
        <SimpleGrid cols={4} mb={24}>
          <Paper className={classes.dashCard}>
            <Text className={classes.scoreCardTitle}>Completion Rate</Text>
            <Text className={classes.scoreCardValue} color={getScoreColor(ops.score ?? 0)}>
              {ops.score ?? 0}%
            </Text>
            <Box className={classes.scoreBar}>
              <Box style={{
                height: "100%",
                width: `${ops.score ?? 0}%`,
                borderRadius: 4,
                background: `linear-gradient(90deg, ${getScoreColor(ops.score ?? 0)}, ${getScoreColor(ops.score ?? 0)}88)`,
                transition: "width 0.6s ease",
              }} />
            </Box>
          </Paper>
          <Paper className={classes.dashCard}>
            <Text className={classes.scoreCardTitle}>Total Tasks (30d)</Text>
            <Text className={classes.scoreCardValue}>{ops.totalTasks ?? 0}</Text>
          </Paper>
          <Paper className={classes.dashCard}>
            <Text className={classes.scoreCardTitle}>Completed</Text>
            <Text className={classes.scoreCardValue} color="#10B981">{ops.completedTasks ?? 0}</Text>
          </Paper>
          <Paper className={classes.dashCard}>
            <Text className={classes.scoreCardTitle}>Overdue</Text>
            <Text className={classes.scoreCardValue} color="#EF4444">{ops.overdueTasks ?? 0}</Text>
          </Paper>
        </SimpleGrid>
      </Box>
    );
  };

  const renderAlerts = () => (
    <Box>
      <Text className={classes.title}>Compliance Alerts</Text>
      <Box className={classes.table}>
        <ScrollArea>
          <Table withBorder fontSize="sm" verticalSpacing="md" highlightOnHover>
            <thead>
              <tr>
                <th style={{ minWidth: 120 }}>Category</th>
                <th style={{ minWidth: 200 }}>Item</th>
                <th style={{ minWidth: 120 }}>Status</th>
                <th style={{ minWidth: 150 }}>Expiration</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4}><Flex justify="center" py={30}><Loader color="#FFAF4A" size="sm" /></Flex></td></tr>
              ) : alerts.length > 0 ? (
                alerts.map((a, i) => (
                  <tr key={i}>
                    <td><Badge variant="light" color={a.alertCategory === "staff" ? "blue" : a.alertCategory === "client" ? "teal" : "violet"}>{a.alertCategory}</Badge></td>
                    <td>{a.credentialType?.name || a.complianceItem?.name || "\u2014"}</td>
                    <td><Badge color={statusColor(a.status)} className={classes.statusBadge}>{a.status}</Badge></td>
                    <td>{a.expirationDate ? moment(a.expirationDate).format("MMM DD, YYYY") : "\u2014"}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4}><RecordNotFound /></td></tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
        <CustomBillingPagination
          data={alertsCount}
          itemsPerPage={20}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      </Box>
    </Box>
  );

  const renderSettings = () => {
    if (!settingsForm) {
      return <Flex align="center" justify="center" py={60}><Loader color="#FFAF4A" /></Flex>;
    }

    const totalWeight = (settingsForm.credentialWeight || 0) + (settingsForm.notesWeight || 0) +
      (settingsForm.marWeight || 0) + (settingsForm.taskWeight || 0);

    return (
      <Box>
        {/* Alert Thresholds */}
        <Box className={classes.settingsCard}>
          <Text className={classes.settingsTitle}>Alert Thresholds</Text>
          <Text className={classes.settingsDescription} mt={4}>
            Set how many days before expiration an item should be flagged as "expiring soon".
          </Text>
          <Divider my="sm" />
          <Grid mt={10}>
            <Grid.Col span={4}>
              <NumberInput
                label="Staff Credential Alert (days)"
                placeholder="30"
                min={1}
                max={365}
                radius="md"
                value={settingsForm.credentialExpiryAlertDays}
                onChange={(val) => handleSettingsChange("credentialExpiryAlertDays", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Client Compliance Alert (days)"
                placeholder="30"
                min={1}
                max={365}
                radius="md"
                value={settingsForm.clientComplianceAlertDays}
                onChange={(val) => handleSettingsChange("clientComplianceAlertDays", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Facility Compliance Alert (days)"
                placeholder="30"
                min={1}
                max={365}
                radius="md"
                value={settingsForm.facilityComplianceAlertDays}
                onChange={(val) => handleSettingsChange("facilityComplianceAlertDays", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Documentation Thresholds */}
        <Box className={classes.settingsCard} mt={20}>
          <Text className={classes.settingsTitle}>Documentation Thresholds</Text>
          <Text className={classes.settingsDescription} mt={4}>
            Target completion percentages for documentation. Staff scoring below these thresholds may be flagged.
          </Text>
          <Divider my="sm" />
          <Grid mt={10}>
            <Grid.Col span={4}>
              <NumberInput
                label="Notes Threshold (%)"
                placeholder="95"
                min={0}
                max={100}
                radius="md"
                value={settingsForm.notesThreshold}
                onChange={(val) => handleSettingsChange("notesThreshold", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="MAR Threshold (%)"
                placeholder="95"
                min={0}
                max={100}
                radius="md"
                value={settingsForm.marThreshold}
                onChange={(val) => handleSettingsChange("marThreshold", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Task Threshold (%)"
                placeholder="90"
                min={0}
                max={100}
                radius="md"
                value={settingsForm.taskThreshold}
                onChange={(val) => handleSettingsChange("taskThreshold", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Staff Score Weights */}
        <Box className={classes.settingsCard} mt={20}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text className={classes.settingsTitle}>Staff Compliance Score Weights</Text>
              <Text className={classes.settingsDescription} mt={4}>
                Configure how each factor contributes to a staff member's overall compliance score. Weights must total 100%.
              </Text>
            </Box>
            <Badge
              size="lg"
              radius="md"
              color={totalWeight === 100 ? "green" : "red"}
              variant="light"
              style={{ fontFamily: "Inter" }}
            >
              Total: {totalWeight}%
            </Badge>
          </Flex>
          <Divider my="sm" />
          <Grid mt={10}>
            <Grid.Col span={3}>
              <NumberInput
                label="Credentials"
                placeholder="40"
                min={0}
                max={100}
                radius="md"
                value={settingsForm.credentialWeight}
                onChange={(val) => handleSettingsChange("credentialWeight", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <NumberInput
                label="Notes"
                placeholder="30"
                min={0}
                max={100}
                radius="md"
                value={settingsForm.notesWeight}
                onChange={(val) => handleSettingsChange("notesWeight", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <NumberInput
                label="MAR"
                placeholder="20"
                min={0}
                max={100}
                radius="md"
                value={settingsForm.marWeight}
                onChange={(val) => handleSettingsChange("marWeight", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <NumberInput
                label="Tasks"
                placeholder="10"
                min={0}
                max={100}
                radius="md"
                value={settingsForm.taskWeight}
                onChange={(val) => handleSettingsChange("taskWeight", val)}
                styles={{ label: { fontFamily: "Inter", fontWeight: 500, marginBottom: 4 } }}
              />
            </Grid.Col>
          </Grid>
        </Box>

        {/* Save / Cancel */}
        <Box className={classes.settingsBtnGroup} mt={20}>
          <Button
            color="gray"
            size="md"
            radius="md"
            fullWidth
            onClick={handleResetSettings}
          >
            Cancel
          </Button>
          <Button
            color="yellow"
            size="md"
            radius="md"
            fullWidth
            loading={savingSettings}
            onClick={handleSaveSettings}
          >
            Save
          </Button>
        </Box>
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview": return renderOverview();
      case "regulatory": return renderRegulatory();
      case "charting": return renderCharting();
      case "operational": return renderOperational();
      case "alerts": return renderAlerts();
      case "settings": return renderSettings();
      default: return renderOverview();
    }
  };

  const getAddButton = () => {
    if (activeTab === "regulatory") {
      return (
        <NewAddButton
          title="Add Compliance Item"
          icon={<FaPlus />}
          onClick={() => openModal()}
          color="#FFAF4A"
          textColor="#000"
          variant="filled"
          borderColor="none"
        />
      );
    }
    if (activeTab === "charting") {
      return (
        <NewAddButton
          title="Add Charting Rule"
          icon={<FaPlus />}
          onClick={() => { setEditingClientItem(null); setClientItemCategory("notes"); openClientModal(); }}
          color="#FFAF4A"
          textColor="#000"
          variant="filled"
          borderColor="none"
        />
      );
    }
    if (activeTab === "operational") {
      return (
        <NewAddButton
          title="Add Operational Rule"
          icon={<FaPlus />}
          onClick={() => { setEditingClientItem(null); setClientItemCategory("task"); openClientModal(); }}
          color="#FFAF4A"
          textColor="#000"
          variant="filled"
          borderColor="none"
        />
      );
    }
    return null;
  };

  return (
    <Box className={classes.content}>
      <AppNavbar title="Compliance" profile={profile} />

      <Box className={classes.mainContainer}>
        {/* Tab bar + action buttons row */}
        <Box className={classes.tabsBar}>
          <Box className={classes.tabContainer}>
            <Box
              className={`${classes.tabBtn} ${activeTab === "overview" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("overview")}
            >
              Overview
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "regulatory" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("regulatory")}
            >
              Regulatory
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "charting" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("charting")}
            >
              Charting
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "operational" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("operational")}
            >
              Operational
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "alerts" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("alerts")}
            >
              Alerts{" "}
              {alertsCount > 0 && (
                <Badge size="xs" color="red" ml={4} style={{ verticalAlign: "middle" }}>{alertsCount}</Badge>
              )}
            </Box>
            <Box
              className={`${classes.tabBtn} ${activeTab === "settings" ? classes.activeTabBg : ""}`}
              onClick={() => handleTabChange("settings")}
            >
              Settings
            </Box>
          </Box>

          <div style={{ display: "flex", gap: 10 }}>
            {getAddButton()}
          </div>
        </Box>

        {/* Tab content */}
        {renderTabContent()}
      </Box>

      <AddComplianceItemModal
        opened={modalOpened}
        onClose={closeModal}
        addLoading={addLoading}
        facilities={facilities}
        noteTypes={noteTypes}
        taskTypes={taskTypes}
      />
      <ClientComplianceItemModal
        opened={clientModalOpened}
        onClose={closeClientModal}
        editItem={editingClientItem}
        addLoading={addLoading}
        noteTypes={noteTypes}
        taskTypes={taskTypes}
        defaultCategory={clientItemCategory}
      />
      <FacilityRequirementModal
        opened={facilityModalOpened}
        onClose={closeFacilityModal}
        editItem={editingFacilityItem}
        facilities={facilities}
        addLoading={addLoading}
      />

      {/* ── Audit Modal ── */}
      <Modal
        opened={auditModalOpened}
        onClose={closeAuditModal}
        title={<Text ff="Inter" fw={700} fz={18}>{getAuditTitle(auditType)}</Text>}
        size="xl"
        centered
        styles={{
          header: { borderBottom: "1px solid #E5E7EB", paddingBottom: 12 },
          body: { padding: "16px 24px" },
        }}
      >
        {auditLoading ? (
          <Flex justify="center" align="center" py={60}>
            <Loader size="md" />
          </Flex>
        ) : auditData ? (
          <>
            <Group position="apart" mb={16}>
              <Group spacing={12}>
                <Badge size="lg" variant="light" color="red" radius="sm" ff="Inter">
                  {auditData.total} Client{auditData.total !== 1 ? "s" : ""} Non-Compliant
                </Badge>
                <Badge size="lg" variant="light" color="gray" radius="sm" ff="Inter">
                  30-Day Period
                </Badge>
              </Group>
              {auditData.period && (
                <Text fz={12} ff="Inter" color="#6B7280">
                  {moment(auditData.period.from).format("MMM D")} — {moment(auditData.period.to).format("MMM D, YYYY")}
                </Text>
              )}
            </Group>

            {auditData.clients?.length === 0 ? (
              <Flex direction="column" align="center" py={40}>
                <ThemeIcon size={48} radius="xl" color="green" variant="light" mb={12}>
                  <IconShieldCheck size={24} />
                </ThemeIcon>
                <Text ff="Inter" fw={600} fz={16} color="#1F2937">All Clear!</Text>
                <Text ff="Inter" fz={13} color="#6B7280" mt={4}>All clients are compliant for this period.</Text>
              </Flex>
            ) : (
              <ScrollArea style={{ maxHeight: 450 }}>
                <Table striped highlightOnHover withBorder withColumnBorders fontSize="sm"
                  styles={{ root: { fontFamily: "Inter" } }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#F9FAFB" }}>
                      <th style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 12, color: "#374151", padding: "10px 12px" }}>Client Name</th>
                      <th style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 12, color: "#374151", padding: "10px 12px", width: 120, textAlign: "center" }}>
                        {getAuditMissingLabel(auditType)}
                      </th>
                      <th style={{ fontFamily: "Inter", fontWeight: 600, fontSize: 12, color: "#374151", padding: "10px 12px" }}>Missing Dates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.clients?.map((client, idx) => (
                      <tr key={client.clientId || idx}>
                        <td style={{ fontFamily: "Inter", fontSize: 13, fontWeight: 500, padding: "10px 12px", color: "#1F2937" }}>
                          {client.clientName}
                        </td>
                        <td style={{ textAlign: "center", padding: "10px 12px" }}>
                          <Badge
                            size="lg"
                            variant="light"
                            color={client.missingCount > 20 ? "red" : client.missingCount > 10 ? "orange" : "yellow"}
                            radius="sm"
                            ff="Inter"
                          >
                            {client.missingCount}
                          </Badge>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <Flex wrap="wrap" gap={4}>
                            {client.missingDates?.slice(0, 2).map((date) => (
                              <Badge
                                key={date}
                                size="sm"
                                variant="outline"
                                color="gray"
                                radius="sm"
                                ff="Inter"
                              >
                                {moment(date).format("MMM D")}
                                {auditType === "mar" && client.missedPerDate?.[date] ? ` (${client.missedPerDate[date]})` : ""}
                              </Badge>
                            ))}
                            {client.missingDates?.length > 2 && (
                              <Badge size="sm" variant="light" color="gray" radius="sm" ff="Inter">
                                +{client.missingDates.length - 2} more
                              </Badge>
                            )}
                          </Flex>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollArea>
            )}

            {auditData.error && (
              <Text fz={12} ff="Inter" color="#EF4444" mt={12}>{auditData.error}</Text>
            )}
          </>
        ) : (
          <Text ff="Inter" fz={14} color="#6B7280" py={40} align="center">
            No audit data available.
          </Text>
        )}
      </Modal>
    </Box>
  );
};

export default CompliancePage;
